import React from 'react';
import styles from '../styles/components/PanelSummaryModal.module.css';
import Modal from '@ax2/common/components/Modal';
import { SimpleQuestionsPreview } from '@ax2/takesurvey/components/SimpleQuestionsPreview';
import PanelType, { getPanelTypeText } from '@ax2/common/model/PanelType';
import UUID from '@ax2/common/model/UUID';
import QuestionType from '@ax2/common/model/QuestionType';
import { GridRequiredStrategy } from '@ax2/common/model/schemas/GridRequiredOptions';
import AnswerType from '@ax2/common/model/AnswerType';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import { getSummaryByPanel } from 'contexts/PanelRecommendationContext';
import AnswerData from '@ax2/common/model/answers-data/AnswerData';

interface PanelSummaryModalData {
  panelType: PanelType;
  summary?: JSX.Element;
  onClose: () => void;
}

const PanelSummaryModal: React.FC<PanelSummaryModalData> = ({
  summary,
  panelType,
  onClose,
}) => {
  const selectionSummary = summary ? summary : getSummaryByPanel(panelType);
  const getSampleSize = (panel: PanelType) => {
    let sampleSize;
    if (panel === PanelType.OncologyCenter) {
      sampleSize = '15';
    } else if (panel === PanelType.PhysicianSurvey) {
      sampleSize = '40';
    } else {
      sampleSize = '20';
    }
    return sampleSize;
  };
  return (
    <Modal onClose={onClose}>
      <div className={styles.modalContents}>
        {selectionSummary && (
          <div className={styles.panelInfo}>
            <span className={styles.title}>Panel Summary</span>
            <span className={styles.subtitle}>
              {`${getPanelTypeText(panelType)} Panel – Summary`}
            </span>
            <span className={styles.description}>{selectionSummary}</span>
            <div className={styles.additionalInfo}>
              <span className={styles.infoTitle}>Sample size:</span>
              <span className={styles.info}> </span>
              <span className={styles.info}>{getSampleSize(panelType)}</span>
            </div>
            <div>
              <span className={styles.infoTitle}>Roles included:</span>
              <span className={styles.info}> </span>
              <span className={styles.info}>
                Medical and pharmacy executives (director level and above)
              </span>
            </div>
          </div>
        )}
        <div className={styles.infoSeparator}></div>
        <div className={styles.panelInfo}>
          <span className={styles.title}>Panel Survey Preview</span>
          <span className={styles.subtitle}>
            Panel Survey Template – Sample Question
          </span>
          <span className={styles.description}>
            You will be able to choose from any of our panel survey templates
            from our template library, or you can build your own custom panel
            survey. Below are two example questions.
          </span>
          <div className={styles.additionalInfo}>
            <span className={styles.infoTitle}>
              Number of questions allowed in survey:
            </span>
            <span className={styles.info}> </span>
            <span className={styles.info}>Up to 10 & up to 3 open-ended </span>
          </div>
        </div>
        <div className={styles.preview}>
          <SimpleQuestionsPreview
            questions={defQuestions}
            answers={defAnswers}
          />
        </div>
      </div>
    </Modal>
  );
};

export default PanelSummaryModal;

const defQuestions: QuestionSchema[] = [
  {
    id: '111' as UUID,
    questionTemplateId: null,
    order: 0,
    questionType: QuestionType.RadioButtonGrid,
    isExclusivePerRow: false,
    isRequired: true,
    isLocked: true,
    requiredOptions: {
      strategy: GridRequiredStrategy.AtLeastOnePerColumn,
    },
    randomizeRow: false,
    randomizeColumn: false,
    title: null,
    imageUrl: null,
    content: {
      template:
        'How is your organization covering the products below on your organization`s most common formulary (e.g., commercial, Medicare/Part D, Medicaid)',
    },
    description: null,
    columns: [
      {
        id: '1' as UUID,
        order: 0,
        label: null,
        content: { template: 'Product A' },
        typ: AnswerType.Standard,
      },
      {
        id: '2' as UUID,
        order: 1,
        label: null,
        content: { template: 'Product B' },
        typ: AnswerType.Standard,
      },
      {
        id: '3' as UUID,
        order: 2,
        label: null,
        content: { template: 'Product C' },
        typ: AnswerType.Standard,
      },
      {
        id: '4' as UUID,
        order: 2,
        label: null,
        content: { template: 'Product D' },
        typ: AnswerType.Standard,
      },
    ],
    rows: [
      {
        id: '10' as UUID,
        order: 0,
        label: null,
        content: { template: 'Preferred tier' },
        typ: AnswerType.Standard,
      },
      {
        id: '20' as UUID,
        order: 1,
        label: null,
        content: { template: 'Non-preferred tier' },
        typ: AnswerType.Standard,
      },
      {
        id: '30' as UUID,
        order: 2,
        label: null,
        content: { template: 'Specialty tier' },
        typ: AnswerType.Standard,
      },
      {
        id: '40' as UUID,
        order: 3,
        label: null,
        content: { template: 'Not covered' },
        typ: AnswerType.Standard,
      },
      {
        id: '50' as UUID,
        order: 3,
        label: null,
        content: { template: 'Don`t know' },
        typ: AnswerType.Standard,
      },
    ],
  },
  {
    id: '222' as UUID,
    questionTemplateId: null,
    order: 1,
    questionType: QuestionType.RadioButtonList,
    isRequired: true,
    isLocked: true,
    randomize: false,
    requiredOptions: null,
    title: null,
    imageUrl: null,
    content: {
      template:
        'How is your organization likely to cover this product on your organization’s most common formulary (e.g., commercial, Medicare/Part D, Medicaid)?',
    },
    description: null,
    answerOptions: [
      {
        id: '2220' as UUID,
        order: 0,
        label: null,
        content: { template: 'Preferred tier' },
        typ: AnswerType.Standard,
      },
      {
        id: '2221' as UUID,
        order: 1,
        label: null,
        content: { template: 'Non-preferred tier' },
        typ: AnswerType.Standard,
      },
      {
        id: '2222' as UUID,
        order: 2,
        label: null,
        content: { template: 'Specialty tier' },
        typ: AnswerType.Standard,
      },
    ],
  },
];

const defAnswers: AnswerData[] = [
  {
    questionId: '111' as UUID,
    selected: [
      {
        columnId: '1' as UUID,
        rowId: '10' as UUID,
      },
      {
        columnId: '2' as UUID,
        rowId: '20' as UUID,
      },
      {
        columnId: '3' as UUID,
        rowId: '30' as UUID,
      },
      {
        columnId: '4' as UUID,
        rowId: '40' as UUID,
      },
    ],
    participantDefinedOption: null,
    id: '222dsad' as UUID,
  },
  {
    questionId: '222' as UUID,
    selectedId: '2220' as UUID,
    participantDefinedOption: null,
    id: '2asdas22' as UUID,
  },
];
